import { useMemo } from "react";
import { z } from "zod";
import {
  constructCurrency,
  convertAmountToCents,
  convertCentsToAmount,
} from "common/currency";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useWithdrawalSettings } from "@/hooks/firestore/user/useWithdrawalSettings";

type WithdrawAmountLimits = {
  /** Smallest permitted withdrawal, in dollars. */
  min: number;
  /** Largest permitted single withdrawal, in dollars. Omitted where uncapped. */
  max?: number;
};

const DEFAULT_LIMITS: WithdrawAmountLimits = { min: 0.01 };

// Interac caps a single transfer; AUD and NZD are bounded only by the
// withdrawable balance.
const LIMITS_BY_CURRENCY: Record<string, WithdrawAmountLimits> = {
  AUD: { min: 0.01 },
  NZD: { min: 0.01 },
  CAD: { min: 20, max: 25_000 },
};

const buildWithdrawAmountSchema = ({
  currency,
  withdrawableAmountInCents,
}: {
  currency: string;
  withdrawableAmountInCents: number;
}) => {
  const { min, max } = LIMITS_BY_CURRENCY[currency] ?? DEFAULT_LIMITS;
  const format = (cents: number) =>
    constructCurrency(cents, { currencyType: currency });

  let amount = z
    .number({ error: "Withdraw Amount should not be empty" })
    .min(min, `Amount must be ${format(convertAmountToCents(min))}, or more`);

  if (max !== undefined) {
    amount = amount.max(
      max,
      `Amount must be ${format(convertAmountToCents(max))}, or less, per transaction`,
    );
  }

  return z.object({
    amount: amount.max(
      convertCentsToAmount(withdrawableAmountInCents),
      `Amount exceeds your withdrawable balance of ${format(withdrawableAmountInCents)}`,
    ),
  });
};

const useWithdrawAmountValidationSchema = () => {
  const currency = useFiatCurrency();
  const { settings, isLoading } = useWithdrawalSettings(currency);

  const schema = useMemo(
    () =>
      buildWithdrawAmountSchema({
        currency,
        withdrawableAmountInCents: settings.withdrawableAmount,
      }),
    [currency, settings.withdrawableAmount],
  );

  return [schema, isLoading] as const;
};

export {
  buildWithdrawAmountSchema,
  useWithdrawAmountValidationSchema,
  LIMITS_BY_CURRENCY,
  type WithdrawAmountLimits,
};
